/**
 * PlumeCoverageMapPage.tsx — Plume Coverage tied to the real module array
 *
 * v54: now reads the actual designed Module Array config (rb_module_array_v1)
 *      and renders modules at their grid-derived positions inside the
 *      reservoir polygon. Surface plume circles drawn at each module's
 *      computed radius. Layer toggles let the user see modules, plumes,
 *      dead zones and the recommended-vs-designed module count side by side.
 */

import { useMemo, useState, useEffect } from 'react';
import { MapPin, Layers, ArrowLeft, RefreshCcw, CheckCircle2, AlertTriangle, Info, Eye, EyeOff } from 'lucide-react';
import { useNavigate } from 'react-router';
import PageHeader from '@/components/PageHeader';
import { loadPersisted, KEYS, onPersistChange } from '@/lib/sessionPersistence';
import {
  specFromLegacy, type LegacyModSpec, type DiffuserSpec,
  surfaceRadiusM, moduleBedCoverageAreaM2, bubbleDiameterMm, sotePerMetre,
  describeShape, PLUME_SHAPE_LABEL,
} from '@/lib/diffuserPhysics';
import AwwaM37Toggle, { useAwwaOverride, awwaClassify } from '@/components/AwwaM37Toggle';

// ─── Reservoir constants (Barcombe) ──────────────────────────────────────────
const RESERVOIR_AREA_M2 = 34_000;
const RESERVOIR_AREA_HA = RESERVOIR_AREA_M2 / 10_000;
const AWWA_MIN_PER_HA = 1;
const AWWA_MAX_PER_HA = 6;
const DEFAULT_OVERLAP_PCT = 15;

const FRAME_SIZE_M = 2.0;

interface ModSpec extends LegacyModSpec {
  waterDepthM: number;
  mountingHeightM: number;
  waterTempC: number;
}

const DEFAULTS: ModSpec = {
  diffuserCount: 8, diffuserType: 'membrane_disc_epdm',
  diffuserDiamMm: 300, poreSizeUm: 80, airflowPerDiffNm3h: 1.2,
  waterDepthM: 3.0, mountingHeightM: 0.40, waterTempC: 12,
};

// Module Array shape
interface ArrayConfig {
  moduleCount: number;
  cols: number;
  gapXM: number;
  gapYM: number;
  reservoirAreaHa: number;
}
const ARRAY_DEFAULTS: ArrayConfig = {
  moduleCount: 6, cols: 2, gapXM: 2.0, gapYM: 2.0, reservoirAreaHa: 3.4,
};
const ARRAY_KEY = 'rb_module_array_v1';

function fmt(n: number, d = 2) {
  if (!isFinite(n)) return '—';
  return n.toLocaleString(undefined, { minimumFractionDigits: d, maximumFractionDigits: d });
}

interface CoverageResult {
  dSpec: DiffuserSpec;
  diffShape: string;
  H_eff: number;
  perDiffRadius_m: number;
  perDiffArea_m2: number;
  perModuleTileArea_m2: number;
  overlapFrac: number;
  effectiveTileArea_m2: number;
  modulesForFullCoverage: number;
  modulesAtAWWAMin: number;
  modulesAtAWWAMax: number;
  awwaOk: boolean;
  deadZonePctIfUnderspec: number;
  d_b_mm: number;
  sotePerM: number;
  // Real-array specific
  designedModuleCount: number;
  designedCoverage_m2: number;
  designedCoveragePct: number;
  designedAwwaOk: boolean;
}

function computeCoverage(spec: ModSpec, overlapPct: number, array: ArrayConfig, awwaOverride: boolean): CoverageResult {
  const dSpec = specFromLegacy(spec);
  const H_eff = Math.max(0.3, spec.waterDepthM - spec.mountingHeightM);
  const perDiffRadius_m = surfaceRadiusM(dSpec, H_eff);
  const perDiffArea_m2 = Math.PI * perDiffRadius_m * perDiffRadius_m;
  const perModuleTileArea_m2 = moduleBedCoverageAreaM2(dSpec, H_eff);
  const overlapFrac = Math.max(0, Math.min(0.4, overlapPct / 100));
  const effectiveTileArea_m2 = perModuleTileArea_m2 * (1 - overlapFrac);
  const modulesForFullCoverage = Math.ceil(RESERVOIR_AREA_M2 / Math.max(1, effectiveTileArea_m2));
  const cls = awwaClassify(modulesForFullCoverage, RESERVOIR_AREA_HA, awwaOverride);
  const modulesAtAWWAMin = cls.min;
  const modulesAtAWWAMax = cls.max;
  const awwaOk = cls.ok;
  const underspec = Math.max(1, Math.round(modulesForFullCoverage * 0.75));
  const deadZonePctIfUnderspec = Math.max(0,
    100 * (1 - (underspec * effectiveTileArea_m2) / RESERVOIR_AREA_M2));
  const d_b_mm = bubbleDiameterMm(dSpec, spec.waterTempC);
  const sotePerM_val = sotePerMetre(d_b_mm);

  // Designed array figures
  const designedModuleCount = array.moduleCount;
  const designedCoverage_m2 = designedModuleCount * effectiveTileArea_m2;
  const designedCoveragePct = Math.min(100, (designedCoverage_m2 / RESERVOIR_AREA_M2) * 100);
  const designedCls = awwaClassify(designedModuleCount, RESERVOIR_AREA_HA, awwaOverride);
  const designedAwwaOk = designedCls.ok;

  return {
    dSpec, diffShape: describeShape(dSpec), H_eff,
    perDiffRadius_m, perDiffArea_m2, perModuleTileArea_m2,
    overlapFrac, effectiveTileArea_m2,
    modulesForFullCoverage, modulesAtAWWAMin, modulesAtAWWAMax, awwaOk,
    deadZonePctIfUnderspec, d_b_mm, sotePerM: sotePerM_val,
    designedModuleCount, designedCoverage_m2, designedCoveragePct, designedAwwaOk,
  };
}

// ─── Reservoir polygon (shared schematic) ────────────────────────────────────
const RESERVOIR_POLY: [number, number][] = [
  [85, 18], [95, 15], [112, 14], [128, 18], [142, 24],
  [155, 34], [163, 48], [168, 65], [170, 82],
  [169, 100], [166, 118], [168, 136],
  [170, 155], [169, 173], [167, 192],
  [162, 210], [156, 228], [148, 245],
  [138, 262], [127, 277], [116, 290],
  [108, 302], [102, 315], [98, 330],
  [94, 345], [91, 360], [89, 375],
  [88, 390], [87, 403], [87, 415], [85, 432],
  [82, 420], [79, 408], [76, 396], [73, 383],
  [70, 368], [67, 352], [64, 336], [60, 320],
  [56, 303], [51, 287], [47, 270],
  [42, 253], [39, 236], [37, 218],
  [35, 200], [34, 182], [35, 165],
  [38, 148], [38, 130], [36, 112],
  [37, 94], [40, 77], [46, 62],
  [52, 48], [60, 35], [72, 25],
];

function pointInPolygon(px: number, py: number, poly: [number, number][]): boolean {
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const xi = poly[i][0], yi = poly[i][1], xj = poly[j][0], yj = poly[j][1];
    const intersect = ((yi > py) !== (yj > py))
      && (px < (xj - xi) * (py - yi) / ((yj - yi) || 1e-9) + xi);
    if (intersect) inside = !inside;
  }
  return inside;
}

/**
 * Compute the actual designed module positions in polygon coordinates.
 * The array is laid out cols × rows in metric coords with gapXM / gapYM
 * spacing, then anchored at the deep-zone centre of the reservoir polygon
 * (approx 95, 200).
 *
 * Polygon coord scale ≈ 1 unit / m.
 */
function designedModulePositions(array: ArrayConfig): { x: number; y: number; idx: number }[] {
  const cols = Math.max(1, Math.min(6, array.cols));
  const rows = Math.ceil(array.moduleCount / cols);
  const pitchX = FRAME_SIZE_M + array.gapXM;   // metres
  const pitchY = FRAME_SIZE_M + array.gapYM;
  const anchorX = 95;
  const anchorY = 200;
  const out: { x: number; y: number; idx: number }[] = [];
  // Centre the grid on the anchor
  const totalW = (cols - 1) * pitchX;
  const totalH = (rows - 1) * pitchY;
  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      const idx = r * cols + c;
      if (idx >= array.moduleCount) break;
      const x = anchorX + (c * pitchX - totalW / 2);
      const y = anchorY + (r * pitchY - totalH / 2);
      out.push({ x, y, idx });
    }
  }
  return out;
}

/**
 * Recommended hex tiling of the reservoir for a target module count.
 * Used when the user toggles "recommended layout" overlay.
 */
function hexPoints(targetCount: number, radius_m: number): { x: number; y: number }[] {
  const out: { x: number; y: number }[] = [];
  const stepX = Math.max(8, radius_m * 1.6);
  const stepY = Math.max(7, radius_m * 1.4);
  const minX = 30, maxX = 175, minY = 12, maxY = 435;
  for (let y = minY; y <= maxY && out.length < targetCount * 3; y += stepY) {
    const row = Math.round((y - minY) / stepY);
    const offsetX = (row % 2) * (stepX / 2);
    for (let x = minX + offsetX; x <= maxX && out.length < targetCount * 3; x += stepX) {
      if (pointInPolygon(x, y, RESERVOIR_POLY)) {
        out.push({ x, y });
        if (out.length >= targetCount) return out;
      }
    }
  }
  return out;
}

// ─── Page component ─────────────────────────────────────────────────────────
export default function PlumeCoverageMapPage() {
  const navigate = useNavigate();
  const [spec, setSpec] = useState<ModSpec>(() => loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS));
  const [array, setArray] = useState<ArrayConfig>(() => loadPersisted<ArrayConfig>(ARRAY_KEY, ARRAY_DEFAULTS));
  const [overlapPct, setOverlapPct] = useState<number>(DEFAULT_OVERLAP_PCT);

  // Layer visibility
  const [layers, setLayers] = useState({
    polygon:        true,
    designedArray:  true,
    plumeCircles:   true,
    recommended:    false,
    deadZones:      true,
    moduleLabels:   true,
  });

  const [nonce, setNonce] = useState(0);

  useEffect(() => {
    const unsub = onPersistChange((key) => {
      if (key === KEYS.DIFFUSER_MODULE || key === KEYS.CUSTOM_DIFFUSER || key === KEYS.CUSTOM_DIFF_EXPORT) {
        setSpec(loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS));
      }
      if (key === ARRAY_KEY) {
        setArray(loadPersisted<ArrayConfig>(ARRAY_KEY, ARRAY_DEFAULTS));
      }
      setNonce(n => n + 1);
    });
    return () => unsub();
  }, []);

  const { enabled: awwaOverride } = useAwwaOverride();
  const result = useMemo(() => computeCoverage(spec, overlapPct, array, awwaOverride), [spec, overlapPct, array, awwaOverride, nonce]);

  // Designed positions from array config
  const designedPositions = useMemo(() => designedModulePositions(array), [array, nonce]);

  // Recommended hex tiling
  // When AWWA is enforced, clamp visualisation to the cap. When the user has
  // BYPASSED AWWA, show the full physics-driven count (capped only at 80 to
  // keep the SVG legible).
  const recommendedPositions = useMemo(
    () => hexPoints(
      awwaOverride
        ? Math.min(result.modulesForFullCoverage, 80)
        : Math.min(result.modulesForFullCoverage, result.modulesAtAWWAMax, 50),
      result.perDiffRadius_m
    ),
    [result.modulesForFullCoverage, result.modulesAtAWWAMax, result.perDiffRadius_m, awwaOverride]
  );

  // Plume circle radius in polygon units (≈ metres)
  const plumeR_px = Math.max(3, result.perDiffRadius_m);

  // Coverage gap area for dead-zone tinting
  const totalDesignedCoverageArea_polyUnits = result.designedCoverage_m2;
  const deadZonePct = Math.max(0, 100 - result.designedCoveragePct);

  return (
    <div className="space-y-4 p-4">
      <PageHeader
        icon={MapPin}
        title="Plume Coverage & Reservoir Tiling"
        subtitle="Live coverage area per diffuser, plotted against the actual designed module array"
        actions={
          <div className="flex items-center gap-2 flex-wrap">
            <AwwaM37Toggle compact />
            <button onClick={() => navigate('/module-array')}
              className="inline-flex items-center gap-2 rounded-lg border border-sky-700 bg-sky-900/30 px-3 py-1.5 text-sm text-sky-200 hover:bg-sky-900/50">
              Edit Array
            </button>
            <button onClick={() => navigate(-1)}
              className="inline-flex items-center gap-2 rounded-lg border border-slate-700 bg-slate-800 px-3 py-1.5 text-sm text-slate-200 hover:bg-slate-700">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
          </div>
        }
      />

      {/* Active session banner */}
      <div className="rounded-xl border border-rb-brand/30 bg-rb-brand/5 p-4">
        <div className="flex items-start gap-3 flex-wrap">
          <Layers className="w-5 h-5 text-rb-brand mt-0.5 shrink-0" />
          <div className="min-w-0 flex-1">
            <div className="text-sm font-semibold text-slate-100">Active session</div>
            <div className="text-xs text-slate-400 mt-0.5">
              {result.diffShape} · {PLUME_SHAPE_LABEL[result.dSpec.shape]}
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              Water depth {fmt(spec.waterDepthM, 1)} m · Mounting {fmt(spec.mountingHeightM, 2)} m · H_eff {fmt(result.H_eff, 2)} m
            </div>
            <div className="text-xs text-slate-400 mt-0.5">
              Designed array: <span className="text-slate-100 font-semibold">{array.moduleCount} modules</span> in {array.cols} columns ({Math.ceil(array.moduleCount / array.cols)} rows), gaps {array.gapXM} × {array.gapYM} m
            </div>
          </div>
          <button
            onClick={() => {
              setSpec(loadPersisted<ModSpec>(KEYS.DIFFUSER_MODULE, DEFAULTS));
              setArray(loadPersisted<ArrayConfig>(ARRAY_KEY, ARRAY_DEFAULTS));
              setNonce(n => n + 1);
            }}
            className="inline-flex items-center gap-1.5 rounded-md border border-slate-700 bg-slate-800 px-2.5 py-1 text-xs text-slate-300 hover:bg-slate-700">
            <RefreshCcw className="w-3.5 h-3.5" /> Reload
          </button>
        </div>
      </div>

      {/* Headline tiles */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Per-diffuser footprint</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(result.perDiffArea_m2, 1)} m²</div>
          <div className="text-xs text-slate-500 mt-1">r {fmt(result.perDiffRadius_m, 2)} m</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Per-module tile (with overlap)</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(result.effectiveTileArea_m2, 1)} m²</div>
        </div>
        <div className={`rounded-xl border-2 p-4 ${result.designedAwwaOk ? 'border-emerald-700 bg-emerald-900/15' : 'border-amber-700 bg-amber-900/15'}`}>
          <div className="text-xs uppercase tracking-wider text-slate-400">Designed coverage</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{fmt(result.designedCoveragePct, 1)} %</div>
          <div className="text-xs text-slate-500 mt-1">{array.moduleCount} modules × {fmt(result.effectiveTileArea_m2, 0)} m²</div>
        </div>
        <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
          <div className="text-xs text-slate-400 uppercase tracking-wider">Recommended for full coverage</div>
          <div className="text-2xl font-bold text-slate-100 mt-1">{result.modulesForFullCoverage}</div>
          <div className="text-xs text-slate-500 mt-1">vs {array.moduleCount} designed</div>
        </div>
        <div className={`rounded-xl border p-4 ${
          awwaOverride ? 'border-violet-700 bg-violet-900/20' :
          (result.awwaOk ? 'border-emerald-700 bg-emerald-900/20' : 'border-amber-700 bg-amber-900/20')
        }`}>
          <div className="text-xs uppercase tracking-wider text-slate-400">AWWA M37 envelope</div>
          <div className="text-2xl font-bold text-slate-100 mt-1 flex items-center gap-2">
            {awwaOverride
              ? <span className="text-violet-300">BYPASSED</span>
              : <>
                  {result.designedAwwaOk ? <CheckCircle2 className="w-6 h-6 text-emerald-400" /> : <AlertTriangle className="w-6 h-6 text-amber-400" />}
                  {result.designedAwwaOk ? 'PASS' : 'CHECK'}
                </>}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {awwaOverride ? `cap removed (was ${result.modulesAtAWWAMin}–${result.modulesAtAWWAMax})` : `band ${result.modulesAtAWWAMin}–${result.modulesAtAWWAMax}`}
          </div>
        </div>
      </div>

      {/* Coverage gap warning */}
      {result.designedCoveragePct < 95 && (
        <div className="rounded-xl border-2 border-amber-700 bg-amber-900/20 p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-300 mt-0.5 shrink-0" />
            <div>
              <div className="text-sm font-bold text-amber-200">Designed array under-covers the reservoir</div>
              <div className="text-xs text-amber-100/80 mt-1">
                Current array of <strong>{array.moduleCount}</strong> modules covers approximately <strong>{fmt(result.designedCoveragePct, 1)} %</strong> of the
                reservoir bed at {overlapPct} % overlap. <strong>{fmt(deadZonePct, 1)} %</strong> of the bed sits outside active aeration.
                Recommended count for continuous coverage: <strong>{result.modulesForFullCoverage}</strong>{awwaOverride ? ' (AWWA cap bypassed — full count shown)' : ''}.
                {!awwaOverride && result.modulesForFullCoverage > result.modulesAtAWWAMax && (
                  <span className="block mt-1 text-violet-200">
                    ⚠ Required count <strong>{result.modulesForFullCoverage}</strong> exceeds the AWWA M37 maximum of <strong>{result.modulesAtAWWAMax}</strong>.
                    Toggle "AWWA M37: BYPASSED" in the header to size the array for full coverage without the cap.
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Layer toggles */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Map layers</div>
        <div className="flex flex-wrap gap-2">
          {([
            ['polygon',       'Reservoir outline'],
            ['designedArray', 'Designed modules'],
            ['plumeCircles',  'Plume circles'],
            ['recommended',   'Recommended layout (overlay)'],
            ['deadZones',     'Dead-zone tint'],
            ['moduleLabels',  'Module labels'],
          ] as const).map(([k, label]) => (
            <button key={k} onClick={() => setLayers(l => ({ ...l, [k]: !l[k] }))}
              className={`inline-flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs transition ${
                layers[k]
                  ? 'border-rb-brand bg-rb-brand/15 text-slate-100'
                  : 'border-slate-700 bg-slate-800 text-slate-400 hover:bg-slate-700'
              }`}>
              {layers[k] ? <Eye className="w-3.5 h-3.5" /> : <EyeOff className="w-3.5 h-3.5" />}
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Overlap slider */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Coverage overlap target</div>
        <div className="flex items-center gap-3">
          <input type="range" min={0} max={35} step={1} value={overlapPct}
            onChange={e => setOverlapPct(Number(e.target.value))}
            className="flex-1" />
          <div className="w-16 text-right text-slate-100 font-semibold">{overlapPct}%</div>
        </div>
      </div>

      {/* Map visual */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2">Reservoir map — designed array vs recommended tiling</div>
        <div className="flex justify-center">
          <svg viewBox="20 0 170 450" className="w-full max-w-md h-auto bg-slate-950 rounded-lg">
            {/* Dead-zone background tint */}
            {layers.deadZones && layers.polygon && (
              <polygon
                points={RESERVOIR_POLY.map(([x, y]) => `${x},${y}`).join(' ')}
                fill="rgba(239, 68, 68, 0.06)"
              />
            )}
            {/* Reservoir polygon outline */}
            {layers.polygon && (
              <polygon
                points={RESERVOIR_POLY.map(([x, y]) => `${x},${y}`).join(' ')}
                fill={layers.deadZones ? 'rgba(56, 189, 248, 0.04)' : 'rgba(56, 189, 248, 0.08)'}
                stroke="#38bdf8" strokeWidth={1}
              />
            )}
            {/* Recommended layout (faint background) */}
            {layers.recommended && recommendedPositions.map((p, i) => (
              <circle key={`r${i}`} cx={p.x} cy={p.y} r={plumeR_px}
                fill="rgba(168, 85, 247, 0.08)" stroke="#a855f7" strokeWidth={0.3}
                strokeDasharray="2 2" />
            ))}
            {/* Designed plume circles */}
            {layers.designedArray && layers.plumeCircles && designedPositions.map((p, i) => (
              <circle key={`p${i}`} cx={p.x} cy={p.y} r={plumeR_px}
                fill="rgba(34, 197, 94, 0.20)" stroke="#22c55e" strokeWidth={0.6} />
            ))}
            {/* Designed modules (squares 2×2 m) */}
            {layers.designedArray && designedPositions.map((p, i) => (
              <g key={`m${i}`}>
                <rect x={p.x - 1} y={p.y - 1} width={2} height={2}
                  fill="#fbbf24" stroke="#0a1628" strokeWidth={0.3} />
                {layers.moduleLabels && (
                  <text x={p.x} y={p.y + 5.5} textAnchor="middle" fontSize={3.5} fill="#fcd34d" fontWeight="bold">
                    M{i + 1}
                  </text>
                )}
              </g>
            ))}
            {/* Caption */}
            <text x={105} y={445} textAnchor="middle" fontSize={6} fill="#94a3b8">
              Barcombe — {RESERVOIR_AREA_M2.toLocaleString()} m² · {array.moduleCount} designed · {recommendedPositions.length} recommended
            </text>
          </svg>
        </div>

        {/* Legend */}
        <div className="mt-3 flex flex-wrap gap-3 text-xs">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-sm bg-amber-300" />
            <span className="text-slate-300">Designed module (2×2 m frame)</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-3 h-3 rounded-full border border-emerald-500 bg-emerald-500/20" />
            <span className="text-slate-300">Designed plume footprint</span>
          </div>
          {layers.recommended && (
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-full border border-violet-500 border-dashed bg-violet-500/15" />
              <span className="text-slate-300">Recommended additional tiling</span>
            </div>
          )}
          {layers.deadZones && (
            <div className="flex items-center gap-1.5">
              <span className="inline-block w-3 h-3 rounded-sm bg-red-500/20 border border-red-700" />
              <span className="text-slate-300">Bed area outside active aeration</span>
            </div>
          )}
        </div>
      </div>

      {/* Designed vs recommended metrics */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="rounded-xl border-2 border-amber-700/40 bg-amber-900/5 p-4">
          <div className="text-sm font-semibold text-amber-200 mb-3">Designed array (from /module-array)</div>
          <div className="space-y-2 text-sm">
            {[
              ['Module count', `${array.moduleCount} (${array.cols} cols × ${Math.ceil(array.moduleCount / array.cols)} rows)`],
              ['Per-module gaps', `${array.gapXM} × ${array.gapYM} m`],
              ['Effective tile each', `${fmt(result.effectiveTileArea_m2, 1)} m²`],
              ['Total active coverage', `${fmt(totalDesignedCoverageArea_polyUnits, 0)} m² (${fmt(result.designedCoveragePct, 1)} %)`],
              ['Bed outside aeration', `${fmt(deadZonePct, 1)} %`],
              ['AWWA M37 compliance', result.designedAwwaOk ? 'PASS' : 'CHECK'],
            ].map(([k, v], i) => (
              <div key={i} className="flex justify-between gap-2 border-b border-amber-800/30 pb-1.5 last:border-0 last:pb-0">
                <span className="text-slate-400">{k}</span>
                <span className="text-slate-100 font-medium text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-xl border-2 border-violet-700/40 bg-violet-900/5 p-4">
          <div className="text-sm font-semibold text-violet-200 mb-3">Recommended for continuous coverage</div>
          <div className="space-y-2 text-sm">
            {[
              ['Modules for full coverage', `${result.modulesForFullCoverage}`],
              ['AWWA M37 minimum', `${result.modulesAtAWWAMin}`],
              ['AWWA M37 maximum', `${result.modulesAtAWWAMax}`],
              ['Bubble Ø at bed', `${fmt(result.d_b_mm, 2)} mm`],
              ['SOTE per metre', `${fmt(result.sotePerM, 2)} %/m`],
              ['Per-diffuser surface radius', `${fmt(result.perDiffRadius_m, 2)} m`],
            ].map(([k, v], i) => (
              <div key={i} className="flex justify-between gap-2 border-b border-violet-800/30 pb-1.5 last:border-0 last:pb-0">
                <span className="text-slate-400">{k}</span>
                <span className="text-slate-100 font-medium text-right">{v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Methodology */}
      <div className="rounded-xl border border-slate-700 bg-slate-900 p-4">
        <div className="text-sm font-semibold text-slate-100 mb-2 flex items-center gap-2">
          <Info className="w-4 h-4 text-rb-brand" /> How positions are derived
        </div>
        <div className="text-xs text-slate-400 leading-relaxed">
          Module positions are read live from the designed array configuration on the{' '}
          <button onClick={() => navigate('/module-array')} className="text-sky-300 underline">Module Array</button>{' '}
          page (cols, rows, gaps). The grid is centred on the deep zone of the reservoir polygon and rendered to
          metric scale. Plume footprints are computed per module from the active diffuser spec and the effective
          submergence. The recommended-tiling overlay is independent — it shows where additional modules would go
          if the array were sized for full continuous coverage rather than the designed count.
        </div>
      </div>
    </div>
  );
}
